import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";

const drawerWidth = 240;

const API_BASE_URL = "https://wsms.aidigitalsec.com";

const TopicEdit = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);

  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedLesson, setSelectedLesson] = useState("");

  const [topicHeading, setTopicHeading] = useState("");
  const [topicContent, setTopicContent] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [existingPdfPath, setExistingPdfPath] = useState("");

  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const fileInputRef = useRef(null);

  // Fetch Teachers
  const fetchTeachers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Teacher/GetTeacher`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      const data = await response.json();
      setTeachers(Array.isArray(data) ? data : []);
      if (data.length > 0) {
        setSelectedTeacher(sessionStorage.getItem("employeeId"));
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      showSnackbar("Error fetching teachers", "error");
    }
  };

  // Fetch Classes
  const fetchClasses = async (teacherId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Teacher/GetClass`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({ teacherId }),
      });
      const data = await response.json();
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      showSnackbar("Error fetching classes", "error");
    }
  };

  // Fetch Sections
  const fetchSections = async (classId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Teacher/ddlSection_clsId`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          teacherId: selectedTeacher,
          classId,
        }),
      });
      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching sections:", error);
      showSnackbar("Error fetching sections", "error");
    }
  };

  // Fetch Subjects
  const fetchSubjects = async (classId, sectionId, teacherId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/AssignmentCreate/Subjectddl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({ classId, sectionId, teacherId }),
      });
      const data = await response.json();
      setSubjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      showSnackbar("Error fetching subjects", "error");
    }
  };

  // Fetch Lessons
  const fetchLessons = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Teacher_Lessoon/ddlLession`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          classId: selectedClass,
          subjectId: selectedSubject,
        }),
      });

      if (!response.ok) throw new Error("Failed to fetch lessons");

      const data = await response.json();
      let lessonsData = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      if (data.data === null && data.msg === "Record Not Found") {
        lessonsData = [];
      }
      setLessons(lessonsData);
    } catch (err) {
      console.error("Lessons error:", err);
      showSnackbar("Failed to load lessons", "error");
    }
  };

  // Fetch Topic Data
  useEffect(() => {
    const fetchTopic = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Teacher_Topic/GetTopic_Teacher`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            teacherId: sessionStorage.getItem("employeeId"),
          }),
        });
        if (response.ok) {
          const data = await response.json();
          const topicToEdit = Array.isArray(data)
            ? data.find((topic) => topic.topicId === parseInt(topicId))
            : data.topics?.find((topic) => topic.topicId === parseInt(topicId));

          if (topicToEdit) {
            setSelectedClass(topicToEdit.classId.toString());
            setSelectedSection(topicToEdit.sectionId?.toString() || "");
            setSelectedSubject(topicToEdit.subjectId?.toString() || "");
            setSelectedLesson(topicToEdit.lessionId?.toString() || "");
            setTopicHeading(topicToEdit.topic || "");
            setTopicContent(topicToEdit.content || "");
            setVideoLink(topicToEdit.videoUrlLink || "");
            setExistingPdfPath(topicToEdit.pdfPath || "");
          } else {
            showSnackbar("Topic not found.", "error");
          }
        } else {
          showSnackbar("Failed to fetch topic details.", "error");
        }
      } catch (error) {
        console.error("Error fetching topic:", error);
        showSnackbar("Error fetching topic details.", "error");
      }
    };

    fetchTopic();
    fetchTeachers();
  }, [topicId]);

  // Show Snackbar
  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Handle Save Topic
  const handleSaveTopic = async () => {
    if (!selectedClass || !selectedSection || !selectedSubject || !topicHeading) {
      showSnackbar("Class, Section, Subject, and Topic Heading are required", "error");
      return;
    }

    if (pdfFile && pdfFile.size > 2 * 1024 * 1024) {
      showSnackbar("File size should not exceed 2 MB", "error");
      return;
    }

    const formData = new FormData();
    formData.append("ClassId", selectedClass);
    formData.append("SectionId", selectedSection);
    formData.append("SubjectId", selectedSubject);
    formData.append("TopicName", topicHeading.trim());
    formData.append("LessionId", selectedLesson || "");
    formData.append("TopicContent", topicContent.trim());
    formData.append("VideoLink", videoLink.trim());
    if (pdfFile) {
      formData.append("PdfPath", pdfFile);
    } else if (existingPdfPath) {
      formData.append("PdfPath", existingPdfPath);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/Teacher_Topic/Id?Id=${topicId}`, {
        method: "PUT",
        headers: {
          Authorization: sessionStorage.getItem("token"),
        },
        body: formData,
      });

      if (response.ok) {
        showSnackbar("Topic updated successfully", "success");
        setTimeout(() => {
          navigate("/topics");
        }, 2000);
      } else {
        throw new Error("Failed to update topic");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Error updating topic", "error");
    }
  };

  // Handle File Change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      showSnackbar("Only PDF files are allowed", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showSnackbar("File size should not exceed 2 MB", "error");
      return;
    }

    setPdfFile(file);
  };

  // Effects
  useEffect(() => {
    if (selectedTeacher) {
      fetchClasses(selectedTeacher);
    } else {
      setClasses([]);
      setSections([]);
      setSubjects([]);
      setLessons([]);
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
      setSubjects([]);
      setLessons([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection && selectedTeacher) {
      fetchSubjects(selectedClass, selectedSection, selectedTeacher);
    } else {
      setSubjects([]);
      setLessons([]);
    }
  }, [selectedClass, selectedSection, selectedTeacher]);

  useEffect(() => {
    if (selectedClass && selectedSubject) {
      fetchLessons();
    } else {
      setLessons([]);
    }
  }, [selectedClass, selectedSubject]);

  return (
    <Box component="main" sx={{
      width: { md: `calc(100% - ${drawerWidth}px)` },
      ml: { md: `${drawerWidth}px` },
      mt: { xs: 0, sm: '64px' },
      p: { xs: 1, md: 3 },
    }}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold", color: "#1976d2" }}>
            Edit Topic
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Teacher</InputLabel>
                <Select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  label="Select Teacher"
                  sx={{ backgroundColor: "white" }}
                  disabled
                >
                  <MenuItem value={sessionStorage.getItem("employeeId")}>
                    {teachers.find((t) => t.employeeId == sessionStorage.getItem("employeeId"))?.employeeName}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedTeacher}>
                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Select Class"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedClass}>
                <InputLabel>Select Section</InputLabel>
                <Select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  label="Select Section"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.sectionId} value={sec.sectionId}>
                      {sec.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSection}>
                <InputLabel>Select Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Select Subject"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Subject</MenuItem>
                  {subjects.map((sub) => (
                    <MenuItem key={sub.subjectId} value={sub.subjectId}>
                      {sub.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSubject}>
                <InputLabel>Select Lesson</InputLabel>
                <Select
                  value={selectedLesson}
                  onChange={(e) => setSelectedLesson(e.target.value)}
                  label="Select Lesson"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Lesson (optional)</MenuItem>
                  {lessons.map((lesson) => (
                    <MenuItem key={lesson.lessionId} value={lesson.lessionId}>
                      {lesson.lessionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Topic Heading
                </Typography>
                <input
                  type="text"
                  value={topicHeading}
                  onChange={(e) => setTopicHeading(e.target.value)}
                  placeholder="Enter Topic Heading"
                  style={{ padding: "10px", width: "100%", borderRadius: "4px", border: "1px solid #ccc" }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Topic Content
                </Typography>
                <ReactQuill
                  value={topicContent}
                  onChange={setTopicContent}
                  placeholder="Enter Topic Content"
                  style={{ height: "200px", marginBottom: "20px" }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Video Link
                </Typography>
                <input
                  type="text"
                  value={videoLink}
                  onChange={(e) => setVideoLink(e.target.value)}
                  placeholder="Enter Video Link"
                  style={{ padding: "10px", width: "100%", borderRadius: "4px", border: "1px solid #ccc" }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Topic File (Max 2 MB, PDF only - optional)
                </Typography>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                {pdfFile && <small>Selected: {pdfFile.name}</small>}
                {existingPdfPath && !pdfFile && (
                  <small>
                    Existing:{" "}
                    <a
                      href={`${API_BASE_URL}/${existingPdfPath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {existingPdfPath.split("/").pop()}
                    </a>
                  </small>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveTopic}
              sx={{ width: "200px" }}
            >
              Update Topic
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => navigate(-1)}
              sx={{ width: "200px" }}
            >
              <ArrowBackIcon sx={{ mr: 1 }} />
              Go Back
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TopicEdit;